import type { Module } from "vuex";

import { SuperAdminRole } from "@/enums/user";
import type { Action, Role } from "@/store/modules/roles";
import type { RootState } from "@/store/types";

const storeNamespace = "auth";

const InternalAuthMutation = {
    SetCreated: "setCreated",
    SetIsAuthorized: "setIsAuthorized",
    SetMe: "setMe",
    SetContentGenerationApiInfo: "setContentGenerationApiInfo",
    SetIsWppLogin: "setIsWppLogin",
    SetWppProjectId: "setWppProjectId",
    SetOauthConfig: "setOauthConfig",
    SetWppTenantId: "SetWppTenantId",
    SetWppWorkspaceId: "SetWppWorkspaceId",
    SetWppDevhubAppVersionId: "SetWppDevhubAppVersionId"
};

export const AuthMutation = {
    SetCreated: `${storeNamespace}/${InternalAuthMutation.SetCreated}`,
    SetIsAuthorized: `${storeNamespace}/${InternalAuthMutation.SetIsAuthorized}`,
    SetMe: `${storeNamespace}/${InternalAuthMutation.SetMe}`,
    SetContentGenerationApiInfo: `${storeNamespace}/${InternalAuthMutation.SetContentGenerationApiInfo}`,
    SetIsWppLogin: `${storeNamespace}/${InternalAuthMutation.SetIsWppLogin}`,
    SetWppProjectId: `${storeNamespace}/${InternalAuthMutation.SetWppProjectId}`,
    SetOauthConfig: `${storeNamespace}/${InternalAuthMutation.SetOauthConfig}`,
    SetWppTenantId: `${storeNamespace}/${InternalAuthMutation.SetWppTenantId}`,
    SetWppWorkspaceId: `${storeNamespace}/${InternalAuthMutation.SetWppWorkspaceId}`,
    SetWppDevhubAppVersionId: `${storeNamespace}/${InternalAuthMutation.SetWppDevhubAppVersionId}`
};

export const AuthGetters = {
    hasScope: `${storeNamespace}/hasScope`,
    isSuperAdmin: `${storeNamespace}/isSuperAdmin`,
    userCan: `${storeNamespace}/userCan`,
    userHasAction: `${storeNamespace}/userHasAction`,
    contentGenerationApiInfo: `${storeNamespace}/contentGenerationApiInfo`,
    isWppLogin: `${storeNamespace}/isWppLogin`,
    wppProjectId: `${storeNamespace}/wppProjectId`,
    oauthConfig: `${storeNamespace}/oauthConfig`,
    wppTenantId: `${storeNamespace}/wppTenantId`,
    wppWorkspaceId: `${storeNamespace}/wppWorkspaceId`,
    wppDevhubAppVersionId: `${storeNamespace}/wppDevhubAppVersionId`
};

export type Scope = {
    campaign: string[] | null;
    client: string[] | null;
    campaignOnlyClient: string[] | null;
    __typename: "UserScope";
};

export type Me = {
    id: string;
    reference: string;
    role: Role;
    scope: Scope;
};

export type State = {
    isAuthorized: boolean;
    me: Me | null;
    contentGenerationApiInfo: string;
    isWppLogin: boolean;
    wppProjectId: string;
    wppTenantId: string;
    oauthConfig: {
        region: string;
        userPoolId: string;
        userPoolWebClientId: string;
        ssoURL: string;
        imageURL: string;
        apiURL: string;
    } | null;
    wppWorkspaceId: string;
    wppDevhubAppVersionId?: string;
};

const getDefaultState = (): State => ({
    isAuthorized: false,
    me: null,
    contentGenerationApiInfo: "",
    isWppLogin: false,
    wppProjectId: "",
    oauthConfig: null,
    wppTenantId: "",
    wppWorkspaceId: "",
    wppDevhubAppVersionId: ""
});

const isSuperAdmin = (state: State) => state.me?.role && state.me.role.name === SuperAdminRole;

const authModule: Module<State, RootState> = {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        hasScope:
            state =>
            (scope: { clientId: string; campaignId: string }, exact = true) => {
                // If the user is a Super Admin grant full access
                if (isSuperAdmin(state)) {
                    return true;
                }
                // If actions on the is an array then look for the passed action otherwise permit no access
                const hasClientScope = !!state.me?.scope.client?.includes(scope.clientId);

                if (typeof scope.campaignId === "undefined" && exact) {
                    return hasClientScope;
                }

                let hasCampaignScope = !!state.me?.scope.campaign?.includes(scope.campaignId);

                if (!exact && !hasCampaignScope) {
                    hasCampaignScope = !!state.me?.scope.campaignOnlyClient?.includes(scope.clientId);
                }

                return hasClientScope || hasCampaignScope;
            },

        isSuperAdmin,

        userCan: (state, getters) => (action: Action) => {
            // If the user is a Super Admin grant full access
            if (isSuperAdmin(state)) {
                return true;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
            return getters.userHasAction(action);
        },

        contentGenerationApiInfo(state) {
            return state.contentGenerationApiInfo;
        },

        userHasAction: state => (action: Action) =>
            state.me?.role && Array.isArray(state.me.role.actions) && state.me.role.actions.includes(action),

        isWppLogin: state => state.isWppLogin,
        wppProjectId: state => state.wppProjectId,
        oauthConfig: state => state.oauthConfig,
        wppTenantId: state => state.wppTenantId,

        wppWorkspaceId: state => state.wppWorkspaceId,

        wppDevhubAppVersionId: state => state.wppDevhubAppVersionId
    },

    mutations: {
        setIsAuthorized(state, payload: State["isAuthorized"]) {
            state.isAuthorized = payload;
        },

        setMe(state, payload: { me: Me }) {
            state.me = payload.me;
        },

        setContentGenerationApiInfo(state, payload: { contentGenerationApiInfo: string }) {
            state.contentGenerationApiInfo = payload.contentGenerationApiInfo;
        },

        setIsWppLogin(state, payload: State["isWppLogin"]) {
            state.isWppLogin = payload;
        },

        setWppProjectId(state, payload: State["wppProjectId"]) {
            state.wppProjectId = payload;
        },

        setOauthConfig(state, payload: State["oauthConfig"]) {
            state.oauthConfig = payload;
        },

        setWppTenantId(state, payload: State["wppTenantId"]) {
            state.wppTenantId = payload;
        },

        setWppWorkspaceId(state, payload: State["wppWorkspaceId"]) {
            state.wppWorkspaceId = payload;
        },
        setWppDevhubAppVersionId(state, payload: State["wppDevhubAppVersionId"]) {
            state.wppDevhubAppVersionId = payload;
        }
    }
};

export default authModule;
